import React, { useState, useCallback, useEffect, useRef } from 'react';
import logo from '@/assets/logo.png';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { LabelCanvas } from '@/components/LabelCanvas';
import { ElementToolbar } from '@/components/ElementToolbar';
import { ElementProperties } from '@/components/ElementProperties';
import { BatchDataForm } from '@/components/BatchDataForm';
import { JsonEditor } from '@/components/JsonEditor';
import { AiLabelCreator } from '@/components/AiLabelCreator';
import { PreviewPanel } from '@/components/PreviewPanel';
import { PrintPanel } from '@/components/PrintPanel';
import { LayersPanel } from '@/components/LayersPanel';
import { TemplateManager } from '@/components/TemplateManager';
import { TemplatePresets } from '@/components/TemplatePresets';
import { usePresets } from '@/hooks/usePresets';
import { MobileNav } from '@/components/MobileNav';
import { MobileSheet } from '@/components/MobileSheet';
import { ThemeToggle } from '@/components/ThemeToggle';
import { SettingsDialog } from '@/components/SettingsDialog';
import { LabelSizePopover } from '@/components/LabelSizePopover';
import { ReleaseNotesDialog } from '@/components/ReleaseNotesDialog';
import { useTemplate } from '@/hooks/useTemplate';
import { useHistory } from '@/hooks/useHistory';
import { useFieldHistory } from '@/hooks/useFieldHistory';
import { useTemplateStorage } from '@/hooks/useTemplateStorage';
import { useCsvData } from '@/hooks/useCsvData';
import { useDeviceSize } from '@/hooks/use-mobile';
import { useClipboard } from '@/hooks/useClipboard';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useActivityLogger } from '@/hooks/useActivityLogger';
import { useLabelHistory } from '@/hooks/useLabelHistory';
import { exportLabelPdf, exportMultiLabelPdf } from '@/lib/pdfExport';
import { printLabels } from '@/lib/browserPrint';
import { 
  LabelTemplate,
  LabelSheetConfig,
  LABEL_SHEET_PRESETS,
  LABEL_WIDTH_MM,
  LABEL_HEIGHT_MM,
  FieldData,
  createSingleLabelConfig,
} from '@/types/label';
import { 
  Layers, 
  Database, 
  Eye, 
  Code,
  Grid3X3,
  FolderOpen,
  LayoutTemplate,
  PanelLeftClose,
  PanelLeft,
  Printer,
  Settings2,
  Sparkles,
  HelpCircle,
  Shield,
  LogOut,
  ScanBarcode,
  History,
  ZoomIn,
  ZoomOut,
  RotateCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const Index = () => {
  const navigate = useNavigate();
  const { user, isAdmin, loading: authLoading, hasPermission, signOut } = useAuth();
  const { logActivity } = useActivityLogger();
  const { saveLabelPrint } = useLabelHistory();
  const {
    template,
    setTemplate,
    selectedElementId,
    setSelectedElementId,
    selectedElement,
    addElement,
    updateElement,
    deleteElement,
    duplicateElement,
    reorderElement,
    moveElement,
    updateTemplateFromJson,
    loadTemplate,
    detectedFields,
    fieldData,
    updateFieldData,
    setAllFieldData,
    resolveContent,
    updateLabelSize,
  } = useTemplate();

  const {
    pushState,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory,
  } = useHistory(template);

  const {
    savedTemplates,
    globalTemplates,
    saveTemplate,
    deleteTemplate,
    duplicateTemplate,
    exportTemplate,
    importTemplate,
    updateCategory,
  } = useTemplateStorage();

  const {
    pushFieldState,
    undoField,
    redoField,
    canUndoField,
    canRedoField,
    clearFieldHistory,
  } = useFieldHistory(fieldData);

  const {
    csvData,
    csvHeaders,
    currentRowIndex,
    totalRows,
    getCurrentRow,
    nextRow,
    prevRow,
    goToRow,
    importCSV,
    importText,
    clearCSV,
  } = useCsvData();

  const { isMobile, isTablet, isSmallPhone, isPortrait, viewportWidth } = useDeviceSize();
  const { presets, activePresets, loading: presetsLoading, createTemplate: createPresetTemplate } = usePresets();
  const { copyElement, pasteElement, hasClipboard } = useClipboard();
  const [activeTab, setActiveTab] = useState('editor');
  const [showGrid, setShowGrid] = useState(true);
  const [gridSize, setGridSize] = useState(1);
  const [showLeftPanel, setShowLeftPanel] = useState(true);
  const [sheetConfig, setSheetConfig] = useState<LabelSheetConfig>(LABEL_SHEET_PRESETS[0]);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState(template.name);
  const [presetsDialogOpen, setPresetsDialogOpen] = useState(false);
  const [activePresetLinkUrl, setActivePresetLinkUrl] = useState<string | null>(null);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);
  
  // Mobile-specific state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Hide left panel on mobile by default
  useEffect(() => {
    if (isMobile) {
      setShowLeftPanel(false);
    }
  }, [isMobile]);

  // Update field data when CSV row changes
  useEffect(() => {
    const currentRow = getCurrentRow();
    if (currentRow) {
      setAllFieldData(currentRow);
    }
  }, [currentRowIndex, getCurrentRow, setAllFieldData]);

  // Pre-fill fields from URL query parameters (e.g. ?preset=MyPreset&TicketNr=001&Name=Max)
  const urlParamsApplied = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const presetNotFoundRef = useRef(false);
  useEffect(() => {
    if (urlParamsApplied.current) return;
    const params: FieldData = {};
    let hasParams = false;
    let presetParam: string | null = null;
    searchParams.forEach((value, key) => {
      if (key === '__lovable_token') return; // Skip internal params
      if (key === 'preset') {
        presetParam = value;
      } else {
        params[key] = value;
      }
      hasParams = true;
    });
    if (!hasParams) return;

    // Wait for user authentication to fully resolve (including admin status)
    if (!user) return;
    if (authLoading) return;
    if (presetParam && presetsLoading) return;

    // Load preset by name if specified (search ALL presets for URL deep-links)
    if (presetParam) {
      // Wait for presets to load (admins see all, including inactive)
      if (presets.length === 0 && !presetNotFoundRef.current) return;
      const matched = presets.find(p => p.name.toLowerCase() === (presetParam as string).toLowerCase());
      if (!matched) {
        // If this is the first miss, wait one more cycle in case presets are reloading after admin status resolved
        if (!presetNotFoundRef.current) {
          presetNotFoundRef.current = true;
          return;
        }
        // Preset truly not found (inactive or doesn't exist)
        urlParamsApplied.current = true;
        setSearchParams({}, { replace: true });
        toast.error(`Preset "${presetParam}" is not available. It may be inactive or does not exist.`);
        return;
      }
      urlParamsApplied.current = true;
      const presetTemplate = createPresetTemplate(matched);
      loadTemplate(presetTemplate);
      clearHistory(presetTemplate);
      setActivePresetLinkUrl(matched.linkUrl);
      setAllFieldData(params);
      setSearchParams({}, { replace: true });
      setActiveTab('print');
    } else {
      // No preset param, just apply field data
      urlParamsApplied.current = true;
      setAllFieldData(params);
      setSearchParams({}, { replace: true });
      setActiveTab('print');
    }
  }, [searchParams, setAllFieldData, setSearchParams, user, authLoading, presets, presetsLoading, createPresetTemplate, loadTemplate, clearHistory]);

  const handlePrintSingle = useCallback(async () => {
    try {
      const singleSheetConfig = createSingleLabelConfig(template.width, template.height);
      await printLabels(template, resolveContent, singleSheetConfig, [fieldData]);
      toast.success('Print dialog opened');
      logActivity('print', 'print', `Printed single label: ${template.name}`, { templateName: template.name });
      const filteredFieldData: FieldData = {};
      detectedFields.forEach(f => { if (fieldData[f] !== undefined) filteredFieldData[f] = fieldData[f]; });
      saveLabelPrint({ template, fieldData: filteredFieldData, copies: 1, printSource: 'editor', sheetConfig: singleSheetConfig });
    } catch (error) {
      toast.error('Failed to open print dialog');
    }
  }, [template, resolveContent, fieldData, detectedFields, logActivity, saveLabelPrint]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!hasPermission('use_keyboard_shortcuts' as any)) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't handle shortcuts when typing in inputs
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) {
        e.preventDefault();
        handleRedo();
      }
      // Copy (Ctrl+C)
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selectedElement && hasPermission('use_clipboard')) {
        e.preventDefault();
        copyElement(selectedElement);
        toast.success('Element copied');
      }
      // Paste (Ctrl+V)
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && hasClipboard && hasPermission('use_clipboard')) {
        e.preventDefault();
        const pastedElement = pasteElement(template.width, template.height);
        if (pastedElement) {
          setTemplate(prev => ({
            ...prev,
            elements: [...prev.elements, pastedElement],
            updatedAt: Date.now(),
          }));
          setSelectedElementId(pastedElement.id);
          pushState(template);
          toast.success('Element pasted');
        }
      }
      // Duplicate (Ctrl+D)
      if ((e.ctrlKey || e.metaKey) && e.key === 'd' && selectedElementId && hasPermission('manage_elements')) {
        e.preventDefault();
        duplicateElement(selectedElementId);
        pushState(template);
      }
      // Print (Ctrl+P)
      if ((e.ctrlKey || e.metaKey) && e.key === 'p' && hasPermission('print_labels')) {
        e.preventDefault();
        handlePrintSingle();
      }
      // Zoom In (Ctrl+=)
      if ((e.ctrlKey || e.metaKey) && (e.key === '=' || e.key === '+')) {
        e.preventDefault();
        handleZoomIn();
      }
      // Zoom Out (Ctrl+-)
      if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        handleZoomOut();
      }
      // Zoom Reset (Ctrl+0)
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        e.preventDefault();
        handleZoomReset();
      }
      // Delete (Delete or Backspace)
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedElementId && hasPermission('manage_elements')) {
        e.preventDefault();
        deleteElement(selectedElementId);
        pushState(template);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedElementId, selectedElement, template, deleteElement, duplicateElement, pushState, handlePrintSingle, copyElement, pasteElement, hasClipboard, setTemplate, setSelectedElementId]);

  const handleUndo = useCallback(() => {
    const prevTemplate = undo();
    if (prevTemplate) {
      setTemplate(prevTemplate);
    }
  }, [undo, setTemplate]);

  const handleRedo = useCallback(() => {
    const nextTemplate = redo();
    if (nextTemplate) {
      setTemplate(nextTemplate);
    }
  }, [redo, setTemplate]);

  const handleHistoryPush = useCallback(() => {
    pushState(template);
  }, [pushState, template]);

  const handleFieldUndo = useCallback(() => {
    if (!hasPermission('use_field_undo' as any)) return;
    const prevData = undoField();
    if (prevData) {
      setAllFieldData(prevData);
    }
  }, [undoField, setAllFieldData, hasPermission]);

  const handleFieldRedo = useCallback(() => {
    if (!hasPermission('use_field_undo' as any)) return;
    const nextData = redoField();
    if (nextData) {
      setAllFieldData(nextData);
    }
  }, [redoField, setAllFieldData, hasPermission]);

  // Track field data changes for undo/redo
  const handleUpdateFieldWithHistory = useCallback((field: string, value: string) => {
    updateFieldData(field, value);
    // Debounce field history push
    const newData = { ...fieldData, [field]: value };
    pushFieldState(newData);
  }, [updateFieldData, fieldData, pushFieldState]);

  // Ctrl+Scroll Wheel Zoom handler
  const handleCanvasWheel = useCallback((e: React.WheelEvent) => {
    if (!hasPermission('use_zoom' as any)) return;
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -10 : 10;
      setEditorZoom(z => Math.min(300, Math.max(25, z + delta)));
    }
  }, [hasPermission]);

  const handleSaveTemplate = useCallback(async () => {
    const saved = await saveTemplate({
      ...template,
      name: templateName,
    });
    if (saved) {
      setTemplate(saved);
      logActivity('template_saved', 'template', `Saved template: ${templateName}`, { templateName });
    }
    setSaveDialogOpen(false);
  }, [template, templateName, saveTemplate, setTemplate, logActivity]);

  const handleLoadTemplate = useCallback((newTemplate: LabelTemplate) => {
    loadTemplate(newTemplate);
    clearHistory(newTemplate);
    toast.success(`Loaded: ${newTemplate.name}`);
  }, [loadTemplate, clearHistory]);

  const handleExportPdf = useCallback(async () => {
    try {
      await exportLabelPdf(template, resolveContent);
      toast.success('PDF exported!');
      logActivity('pdf_export', 'print', `Exported PDF: ${template.name}`, { templateName: template.name });
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  }, [template, resolveContent, logActivity]);

  const handleExportBatch = useCallback(async () => {
    if (csvData.length === 0) {
      toast.error('No CSV data to export');
      return;
    }
    
    try {
      const batchResolve = (content: string, rowData?: Record<string, string>) => {
        return content.replace(/\{\{(\w+)\}\}/g, (match, fieldName) => {
          if (rowData) {
            if (fieldName in rowData && rowData[fieldName] !== undefined) {
              return rowData[fieldName];
            }
            const key = Object.keys(rowData).find(
              k => k.toLowerCase().trim() === fieldName.toLowerCase().trim()
            );
            if (key && rowData[key] !== undefined) {
              return rowData[key];
            }
          }
          if (fieldData[fieldName] !== undefined) {
            return fieldData[fieldName];
          }
          return match;
        });
      };
      
      console.log('Exporting batch with', csvData.length, 'rows:', csvData);
      await exportMultiLabelPdf(template, batchResolve, csvData, sheetConfig);
      toast.success(`Exported ${csvData.length} labels!`);
      logActivity('batch_export', 'print', `Batch exported ${csvData.length} labels: ${template.name}`, { templateName: template.name, rowCount: csvData.length });
    } catch (error) {
      console.error('Batch export error:', error);
      toast.error('Failed to export batch PDF');
    }
  }, [template, csvData, sheetConfig, fieldData, logActivity]);

  const handleImportJson = useCallback(async (file: File) => {
    try {
      const imported = await importTemplate(file);
      if (imported) {
        handleLoadTemplate(imported);
        logActivity('json_import', 'data', `Imported JSON template: ${file.name}`, { fileName: file.name });
      }
    } catch (error) {
      toast.error('Failed to import template');
    }
  }, [importTemplate, handleLoadTemplate, logActivity]);

  const handleImportCsv = useCallback(async (file: File) => {
    try {
      const data = await importCSV(file);
      if (data.length > 0) {
        toast.success(`Imported ${data.length} rows`);
        logActivity('csv_import', 'data', `Imported CSV: ${file.name} (${data.length} rows)`, { fileName: file.name, rowCount: data.length });
      }
    } catch (error) {
      toast.error('Failed to import CSV');
    }
  }, [importCSV, logActivity]);

  const handleImportText = useCallback(
    async (file: File, opts: { mode: 'single' | 'multi'; separator?: string }) => {
      try {
        const rows = await importText(file, { ...opts, templateFields: detectedFields });
        if (rows.length > 0) {
          toast.success(
            rows.length === 1
              ? `Imported 1 label from ${file.name}`
              : `Imported ${rows.length} labels from ${file.name}`
          );
          logActivity('csv_import', 'data', `Imported text file: ${file.name} (${rows.length} records)`, {
            fileName: file.name,
            rowCount: rows.length,
            mode: opts.mode,
          });
        } else {
          toast.error('No key:value pairs found in file');
        }
      } catch (error) {
        toast.error('Failed to import text file');
      }
    },
    [importText, logActivity, detectedFields]
  );

  const handleSelectPreset = useCallback((preset: LabelTemplate) => {
    handleLoadTemplate(preset);
    setPresetsDialogOpen(false);
    // Find matching preset to get linkUrl
    const matched = activePresets.find(p => p.name === preset.name);
    setActivePresetLinkUrl(matched?.linkUrl ?? null);
  }, [handleLoadTemplate, activePresets]);

  const handleNewTemplate = useCallback(() => {
    const newTemplate: LabelTemplate = {
      id: `template-${Date.now()}`,
      name: 'New Template',
      width: LABEL_WIDTH_MM,
      height: LABEL_HEIGHT_MM,
      elements: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    handleLoadTemplate(newTemplate);
  }, [handleLoadTemplate]);

  const handleSubmitForReview = useCallback(async (t: LabelTemplate) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('template_submissions')
        .insert({
          template_id: t.id,
          user_id: user.id,
          template_name: t.name,
          template_snapshot: { ...t } as any,
        });
      if (error) throw error;
      toast.success(`"${t.name}" submitted for admin review`);
    } catch (e) {
      toast.error('Failed to submit template for review');
    }
  }, [user]);

  const handlePrintBatch = useCallback(async (copies: number, sheetConfig: LabelSheetConfig) => {
    try {
      let data: FieldData[];
      if (csvData.length > 0) {
        data = csvData;
      } else {
        data = Array(copies).fill(fieldData);
      }
      await printLabels(template, resolveContent, sheetConfig, data);
      toast.success('Print dialog opened');
      // Save each label in the batch
      for (const row of data) {
        const filteredRow: FieldData = {};
        detectedFields.forEach(f => { if (row[f] !== undefined) filteredRow[f] = row[f]; });
        saveLabelPrint({ template, fieldData: filteredRow, copies: 1, printSource: 'editor', sheetConfig });
      }
    } catch (error) {
      toast.error('Failed to open print dialog');
    }
  }, [template, resolveContent, fieldData, csvData, detectedFields, saveLabelPrint]);

  // Dynamic base scale based on viewport width
  const getBaseScale = useCallback(() => {
    if (viewportWidth < 320) return 1.2;
    if (viewportWidth < 375) return 1.5;
    if (viewportWidth < 414) return 1.8;
    if (isMobile) return 2;
    if (isTablet) return 2.5;
    return 3;
  }, [viewportWidth, isMobile, isTablet]);

  const [editorZoom, setEditorZoom] = useState(100);
  const canvasScale = (getBaseScale() * editorZoom) / 100;

  const handleZoomIn = useCallback(() => setEditorZoom(z => Math.min(300, z + 25)), []);
  const handleZoomOut = useCallback(() => setEditorZoom(z => Math.max(25, z - 25)), []);
  const handleZoomReset = useCallback(() => setEditorZoom(100), []);

  return (
    <TooltipProvider delayDuration={300}>
    <div className="flex flex-col h-screen bg-workspace overflow-hidden">
      {/* Header - Improved UI */}
      <header className="flex items-center justify-between h-12 sm:h-14 px-3 sm:px-4 border-b border-border/50 bg-card shrink-0">
        {/* Left section - Logo and info */}
        <div className="flex items-center gap-2 sm:gap-3">
          <img src={logo} alt="LabelBatch logo" className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-contain" />
           {hasPermission('change_label_size') && (
             <LabelSizePopover
               labelWidth={template.width}
               labelHeight={template.height}
               onLabelSizeChange={updateLabelSize}
             />
           )}
           <Tooltip>
             <TooltipTrigger asChild>
               <Button
                 variant="ghost"
                 size="icon"
                 className="h-8 w-8 text-muted-foreground hover:text-foreground"
                 onClick={() => {
                   const current = template.labelRotation || 0;
                   const next = ((current + 90) % 360) as 0 | 90 | 180 | 270;
                   loadTemplate({ ...template, labelRotation: next, updatedAt: Date.now() });
                 }}
               >
                 <RotateCw className="w-4 h-4" />
                 <span className="sr-only">Rotate Label</span>
               </Button>
             </TooltipTrigger>
             <TooltipContent side="bottom">
               <p>Rotate label ({template.labelRotation || 0}°)</p>
             </TooltipContent>
           </Tooltip>
        </div>

        {/* Center section - Quick actions */}
        <div className="flex items-center gap-1 sm:gap-2">
          {hasPermission('use_presets') && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 h-8 px-2 sm:px-3 border-dashed"
                  onClick={() => setPresetsDialogOpen(true)}
                >
                  <LayoutTemplate className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline text-xs">Templates</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Browse template presets</p>
              </TooltipContent>
            </Tooltip>
          )}
          
          {/* Grid controls - hide on mobile, gate with permission */}
          {hasPermission('use_grid') && (
            <div className="hidden md:flex items-center gap-2 px-2 py-1 rounded-md bg-muted/50">
              <Switch
                id="grid"
                checked={showGrid}
                onCheckedChange={setShowGrid}
                className="scale-90"
              />
              <Label htmlFor="grid" className="text-xs text-muted-foreground cursor-pointer">
                Grid
              </Label>
              {showGrid && (
                <Select value={String(gridSize)} onValueChange={(v) => setGridSize(Number(v))}>
                  <SelectTrigger className="h-6 w-14 text-xs border-0 bg-transparent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.5">0.5mm</SelectItem>
                    <SelectItem value="1">1mm</SelectItem>
                    <SelectItem value="2">2mm</SelectItem>
                    <SelectItem value="5">5mm</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
        </div>

        {/* Right section - Settings and theme */}
        <div className="flex items-center gap-1 sm:gap-1.5">
          {(hasPermission('view_label_history' as any) || hasPermission('print_labels')) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  onClick={() => navigate('/history')}
                >
                  <History className="w-4 h-4" />
                  <span className="sr-only">Label History</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Label History</p>
              </TooltipContent>
            </Tooltip>
          )}
          {hasPermission('use_kiosk') && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  onClick={() => navigate('/kiosk')}
                >
                  <ScanBarcode className="w-4 h-4" />
                  <span className="sr-only">Kiosk Mode</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Kiosk Mode</p>
              </TooltipContent>
            </Tooltip>
          )}

          {isAdmin && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  onClick={() => navigate('/admin')}
                >
                  <Shield className="w-4 h-4" />
                  <span className="sr-only">Admin</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Admin Panel</p>
              </TooltipContent>
            </Tooltip>
          )}

          {hasPermission('view_changelog') && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground relative"
                onClick={() => setReleaseNotesOpen(true)}
              >
                <Sparkles className="w-4 h-4" />
                <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-primary rounded-full" />
                <span className="sr-only">What's New</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>What's new</p>
            </TooltipContent>
          </Tooltip>
          )}
          
          {hasPermission('change_settings') && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-foreground"
                  onClick={() => setSettingsDialogOpen(true)}
                  aria-label="Settings"
                >
                  <Settings2 className="w-4 h-4" />
                  <span className="sr-only">Settings</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>Settings</p>
              </TooltipContent>
            </Tooltip>
          )}
          
          <div className="w-px h-5 bg-border mx-1 hidden sm:block" />
          
          <ThemeToggle />

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={async () => {
                  await signOut();
                  navigate('/auth');
                }}
              >
                <LogOut className="w-4 h-4" />
                <span className="sr-only">Sign Out</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Sign Out</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden min-h-0 min-w-0">
        <ResizablePanelGroup direction="horizontal" className="flex-1">
          {/* Left Panel - Layers/Templates - Hidden on mobile, resizable */}
          {showLeftPanel && !isMobile && (hasPermission('use_layers_panel') || hasPermission('view_templates')) && (
            <>
              <ResizablePanel 
                defaultSize={25} 
                minSize={15} 
                maxSize={40}
                className="bg-card"
              >
                <div className="h-full flex flex-col border-r border-border/50">
                  <Tabs defaultValue={hasPermission('use_layers_panel') ? "layers" : "saved"} className="flex-1 flex flex-col">
                    <TabsList className="h-8 mx-2 mt-2 bg-muted/50 p-0.5">
                      {hasPermission('use_layers_panel') && (
                        <TabsTrigger value="layers" className="text-xs gap-1 flex-1 h-7">
                          <Layers className="w-3 h-3" />
                          Layers
                        </TabsTrigger>
                      )}
                      {hasPermission('view_templates') && (
                        <TabsTrigger value="saved" className="text-xs gap-1 flex-1 h-7">
                          <FolderOpen className="w-3 h-3" />
                          Saved
                        </TabsTrigger>
                      )}
                    </TabsList>
                    
                    {hasPermission('use_layers_panel') && (
                      <TabsContent value="layers" className="flex-1 m-0 mt-2 overflow-hidden">
                        <LayersPanel
                          elements={template.elements}
                          selectedElementId={selectedElementId}
                          onSelectElement={setSelectedElementId}
                          onUpdateElement={(id, updates) => {
                            updateElement(id, updates);
                            pushState(template);
                          }}
                          onDeleteElement={(id) => {
                            deleteElement(id);
                            pushState(template);
                          }}
                          onDuplicateElement={(id) => {
                            duplicateElement(id);
                            pushState(template);
                          }}
                          onReorderElement={(id, dir) => {
                            reorderElement(id, dir);
                            pushState(template);
                          }}
                          onMoveElement={(from, to) => {
                            moveElement(from, to);
                            pushState(template);
                          }}
                        />
                      </TabsContent>
                    )}
                    
                    {hasPermission('view_templates') && (
                      <TabsContent value="saved" className="flex-1 m-0 mt-2 overflow-hidden">
                        <TemplateManager
                          templates={savedTemplates}
                          globalTemplates={globalTemplates}
                          currentTemplateId={template.id}
                          onSelectTemplate={handleLoadTemplate}
                          onDeleteTemplate={hasPermission('delete_templates') ? deleteTemplate : undefined}
                          onDuplicateTemplate={duplicateTemplate}
                          onExportTemplate={exportTemplate}
                          onNewTemplate={handleNewTemplate}
                          onImportTemplate={importTemplate}
                          canDelete={hasPermission('delete_templates')}
                          canManageOwn={hasPermission('manage_own_templates')}
                          canDuplicate={hasPermission('duplicate_templates' as any)}
                          canManageCategories={hasPermission('manage_categories' as any)}
                          canSubmitForReview={hasPermission('submit_templates' as any)}
                          onUpdateCategory={updateCategory}
                          onSubmitForReview={handleSubmitForReview}
                        />
                      </TabsContent>
                    )}
                  </Tabs>
                </div>
              </ResizablePanel>
              <ResizableHandle withHandle />
            </>
          )}

          {/* Main Tabs */}
          <ResizablePanel defaultSize={showLeftPanel && !isMobile ? 85 : 100}>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 h-full flex flex-col min-h-0 overflow-hidden">
          {/* Desktop Tab Bar */}
          {!isMobile && (
            <div className="border-b border-border/50 bg-card flex items-center h-10 shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 ml-1 text-muted-foreground hover:text-foreground"
                onClick={() => setShowLeftPanel(!showLeftPanel)}
              >
                {showLeftPanel ? (
                  <PanelLeftClose className="w-4 h-4" />
                ) : (
                  <PanelLeft className="w-4 h-4" />
                )}
              </Button>
              <TabsList className="h-8 px-1 bg-transparent gap-0.5">
                <TabsTrigger 
                  value="editor" 
                  className="gap-1.5 h-7 px-2 lg:px-3 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
                >
                  <Layers className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Editor</span>
                </TabsTrigger>
                {hasPermission('print_labels') && (
                  <TabsTrigger 
                    value="print" 
                    className="gap-1.5 h-7 px-2 lg:px-3 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
                  >
                    <Printer className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Print</span>
                  </TabsTrigger>
                )}
                {hasPermission('view_data_panel') && (
                  <TabsTrigger 
                    value="data" 
                    className="gap-1.5 h-7 px-2 lg:px-3 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
                  >
                    <Database className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Data</span>
                  </TabsTrigger>
                )}
                {hasPermission('view_preview') && (
                  <TabsTrigger 
                    value="preview" 
                    className="gap-1.5 h-7 px-2 lg:px-3 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Preview</span>
                  </TabsTrigger>
                )}
                {hasPermission('use_json_editor') && (
                  <TabsTrigger 
                    value="json" 
                    className="gap-1.5 h-7 px-2 lg:px-3 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
                  >
                    <Code className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">JSON</span>
                  </TabsTrigger>
                )}
                {hasPermission('use_ai_creator' as any) && (
                  <TabsTrigger 
                    value="ai" 
                    className="gap-1.5 h-7 px-2 lg:px-3 text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">AI</span>
                  </TabsTrigger>
                )}
              </TabsList>
            </div>
          )}

          <TabsContent value="editor" className={`flex-1 flex m-0 overflow-hidden min-h-0 min-w-0 ${isMobile || isPortrait ? 'flex-col' : ''}`}>
            <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden">
              <ElementToolbar 
                onAddElement={(type) => {
                  addElement(type);
                  pushState(template);
                }}
                onUndo={handleUndo}
                onRedo={handleRedo}
                canUndo={canUndo}
                canRedo={canRedo}
                onSave={() => {
                  setTemplateName(template.name);
                  setSaveDialogOpen(true);
                }}
                onExportPdf={handleExportPdf}
                onExportJson={hasPermission('export_json') ? () => exportTemplate(template) : undefined}
                onImportJson={hasPermission('import_templates') ? handleImportJson : undefined}
                onDuplicate={selectedElementId ? () => {
                  duplicateElement(selectedElementId);
                  pushState(template);
                } : undefined}
                hasSelection={!!selectedElementId}
                isMobile={isMobile}
                canManageElements={hasPermission('manage_elements')}
                canExportPdf={hasPermission('export_pdf')}
                canSaveTemplates={hasPermission('manage_own_templates')}
                canUseClipboard={hasPermission('use_clipboard')}
                canExportJson={hasPermission('export_json')}
                canImportTemplates={hasPermission('import_templates')}
              />
              <div className="flex-1 overflow-auto min-h-0 min-w-0 flex flex-col">
                {/* Zoom Controls */}
                <div className="flex items-center justify-between px-3 py-1.5 border-b border-border/50 bg-card/50 shrink-0">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Canvas</span>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleZoomOut} disabled={editorZoom <= 25}>
                      <ZoomOut className="w-3.5 h-3.5" />
                    </Button>
                    <button
                      onClick={handleZoomReset}
                      className="text-[10px] text-muted-foreground hover:text-foreground min-w-[36px] text-center transition-colors"
                    >
                      {editorZoom}%
                    </button>
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleZoomIn} disabled={editorZoom >= 300}>
                      <ZoomIn className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="flex-1 overflow-auto min-h-0 min-w-0" onWheel={handleCanvasWheel}>
              <LabelCanvas
                  template={template}
                  selectedElementId={selectedElementId}
                  onSelectElement={setSelectedElementId}
                  onUpdateElement={updateElement}
                  resolveContent={resolveContent}
                  showGrid={showGrid}
                  gridSize={gridSize}
                  scale={canvasScale}
                  onHistoryPush={handleHistoryPush}
                  enableAlignment={hasPermission('use_alignment')}
                />
                </div>
              </div>
            </div>

            {/* Properties Panel - Shows on side for wide screens, below for portrait/tall/mobile screens */}
            {hasPermission('edit_properties') && (
              (isMobile || isPortrait) ? (
                <div className="h-[40%] min-h-[180px] max-h-[300px] border-t border-border/50 bg-card shrink-0 flex flex-col overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2 border-b border-border/50 shrink-0">
                    <Grid3X3 className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Properties</span>
                  </div>
                  <ScrollArea className="flex-1 min-h-0">
                    <ElementProperties
                      element={selectedElement}
                      onUpdateElement={(id, updates) => {
                        updateElement(id, updates);
                      }}
                      onDeleteElement={(id) => {
                        deleteElement(id);
                        pushState(template);
                      }}
                      labelWidth={template.width}
                      labelHeight={template.height}
                    />
                  </ScrollArea>
                </div>
              ) : (
                <div className="w-64 lg:w-72 xl:w-80 border-l border-border/50 bg-card shrink-0 flex flex-col overflow-hidden">
                  <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border/50 shrink-0">
                    <Grid3X3 className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Properties</span>
                  </div>
                  <ScrollArea className="flex-1 min-h-0">
                    <ElementProperties
                      element={selectedElement}
                      onUpdateElement={(id, updates) => {
                        updateElement(id, updates);
                      }}
                      onDeleteElement={(id) => {
                        deleteElement(id);
                        pushState(template);
                      }}
                      labelWidth={template.width}
                      labelHeight={template.height}
                    />
                  </ScrollArea>
                </div>
              )
            )}
          </TabsContent>

          <TabsContent value="print" className="flex-1 m-0 overflow-auto">
            {hasPermission('print_labels') && (
              <PrintPanel
                template={template}
                fields={detectedFields}
                fieldData={fieldData}
                onUpdateField={handleUpdateFieldWithHistory}
                onFieldUndo={hasPermission('use_field_undo' as any) ? handleFieldUndo : undefined}
                onFieldRedo={hasPermission('use_field_undo' as any) ? handleFieldRedo : undefined}
                canUndoField={canUndoField}
                canRedoField={canRedoField}
                resolveContent={resolveContent}
                csvData={csvData}
                csvHeaders={csvHeaders}
                currentRowIndex={currentRowIndex}
                totalRows={totalRows}
                onImportCsv={importCSV}
                onClearCsv={clearCSV}
                onNextRow={nextRow}
                onPrevRow={prevRow}
                onGoToRow={goToRow}
                onPrintSingle={handlePrintSingle}
                onImportText={handleImportText}
                templateFields={detectedFields}
                onPrintBatch={(copies, config) => {
                  const batchResolve = (content: string, rowData?: Record<string, string>) => {
                    return content.replace(/\{\{(\w+)\}\}/g, (match, fieldName) => {
                      if (rowData) {
                        if (fieldName in rowData && rowData[fieldName] !== undefined) return rowData[fieldName];
                        const key = Object.keys(rowData).find(k => k.toLowerCase().trim() === fieldName.toLowerCase().trim());
                        if (key && rowData[key] !== undefined) return rowData[key];
                      }
                      return fieldData[fieldName] || match;
                    });
                  };
                  return printLabels(template, batchResolve, config, csvData.length > 0 ? csvData : [fieldData]);
                }}
                isMobile={isMobile}
                presetLinkUrl={activePresetLinkUrl}
                onUpdateFieldConfig={(field, key, value) => {
                  setTemplate(prev => ({
                    ...prev,
                    fieldConfig: {
                      ...(prev.fieldConfig || {}),
                      [field]: {
                        ...((prev.fieldConfig || {})[field] || {}),
                        [key]: value,
                      },
                    },
                  }));
                }}
              />
            )}
          </TabsContent>

          <TabsContent value="data" className="flex-1 m-0 overflow-auto">
            {hasPermission('view_data_panel') && (
              <BatchDataForm
                fields={detectedFields}
                fieldData={fieldData}
                onUpdateField={handleUpdateFieldWithHistory}
                csvData={csvData}
                csvHeaders={csvHeaders}
                currentRowIndex={currentRowIndex}
                totalRows={totalRows}
                onImportCsv={importCSV}
                onClearCsv={clearCSV}
                onNextRow={nextRow}
                onPrevRow={prevRow}
                onGoToRow={goToRow}
                sheetConfig={sheetConfig}
                onImportText={handleImportText}
                onSheetConfigChange={setSheetConfig}
                onExportBatch={async () => {
                  const dataRows = csvData.length > 0 ? csvData : [fieldData];
                  const batchResolve = (content: string, rowData?: Record<string, string>) => {
                    return content.replace(/\{\{(\w+)\}\}/g, (match, fieldName) => {
                      if (rowData) {
                        if (fieldName in rowData && rowData[fieldName] !== undefined) return rowData[fieldName];
                        const key = Object.keys(rowData).find(k => k.toLowerCase().trim() === fieldName.toLowerCase().trim());
                        if (key && rowData[key] !== undefined) return rowData[key];
                      }
                      return fieldData[fieldName] || match;
                    });
                  };
                  await exportMultiLabelPdf(template, batchResolve, dataRows, sheetConfig);
                }}
                templateFields={detectedFields}
                canBatchImport={hasPermission('batch_import')}
                canUseDataCarousel={hasPermission('use_data_carousel')}
                canExportPdf={hasPermission('export_pdf')}
              />
            )}
          </TabsContent>

          {hasPermission('view_preview') && (
            <TabsContent value="preview" className="flex-1 m-0 overflow-auto">
              <PreviewPanel
                template={template}
                resolveContent={resolveContent}
              />
            </TabsContent>
          )}

          <TabsContent value="json" className="flex-1 m-0 overflow-auto">
            {hasPermission('use_json_editor') && (
              <JsonEditor
                template={template}
                onUpdateTemplate={(json) => {
                  const success = updateTemplateFromJson(json);
                  if (success) {
                    pushState(template);
                  }
                  return success;
                }}
              />
            )}
          </TabsContent>

          {hasPermission('use_ai_creator' as any) && (
            <TabsContent value="ai" className="flex-1 m-0 overflow-auto">
              <AiLabelCreator
                labelWidth={template.width}
                labelHeight={template.height}
                onApplyElements={(elements, name) => {
                  setTemplate(prev => ({
                    ...prev,
                    elements: elements,
                    name: name || prev.name,
                  }));
                  pushState(template);
                  setActiveTab('editor');
                }}
              />
            </TabsContent>
          )}
        </Tabs>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      {/* Mobile Navigation */}
      {isMobile && (
        <MobileNav
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onMenuClick={() => setMobileMenuOpen(true)}
        />
      )}

      {/* Mobile Menu Sheet */}
      <MobileSheet
        open={mobileMenuOpen}
        onOpenChange={setMobileMenuOpen}
        title="Menu"
      >
        <div className="p-4 space-y-4">
          {/* Layers/Templates */}
          {hasPermission('use_layers_panel') && (
            <div className="space-y-2">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Layers</h3>
              <LayersPanel
                elements={template.elements}
                selectedElementId={selectedElementId}
                onSelectElement={(id) => {
                  setSelectedElementId(id);
                  setMobileMenuOpen(false);
                }}
                onUpdateElement={(id, updates) => {
                  updateElement(id, updates);
                  pushState(template);
                }}
                onDeleteElement={(id) => {
                  deleteElement(id);
                  pushState(template);
                }}
                onDuplicateElement={(id) => {
                  duplicateElement(id);
                  pushState(template);
                }}
                onReorderElement={(id, dir) => {
                  reorderElement(id, dir);
                  pushState(template);
                }}
              />
            </div>
          )}
          
          {/* Saved Templates */}
          {hasPermission('view_templates') && (
            <div className="space-y-2 pt-4 border-t border-border">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Saved Templates</h3>
              <TemplateManager
                templates={savedTemplates}
                globalTemplates={globalTemplates}
                currentTemplateId={template.id}
                onSelectTemplate={(t) => {
                  handleLoadTemplate(t);
                  setMobileMenuOpen(false);
                }}
                onDeleteTemplate={hasPermission('delete_templates') ? deleteTemplate : undefined}
                onDuplicateTemplate={duplicateTemplate}
                onExportTemplate={exportTemplate}
                onNewTemplate={handleNewTemplate}
                onImportTemplate={importTemplate}
                canDelete={hasPermission('delete_templates')}
                canManageOwn={hasPermission('manage_own_templates')}
                canDuplicate={hasPermission('duplicate_templates' as any)}
                canManageCategories={hasPermission('manage_categories' as any)}
                canSubmitForReview={hasPermission('submit_templates' as any)}
                onUpdateCategory={updateCategory}
                onSubmitForReview={handleSubmitForReview}
              />
            </div>
          )}
          
          {/* Grid Settings */}
          {hasPermission('use_grid') && (
            <div className="space-y-2 pt-4 border-t border-border">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Settings</h3>
              <div className="flex items-center justify-between">
                <Label htmlFor="mobile-grid" className="text-sm">Show Grid</Label>
                <Switch
                  id="mobile-grid"
                  checked={showGrid}
                  onCheckedChange={setShowGrid}
                />
              </div>
            </div>
          )}
        </div>
      </MobileSheet>


      {/* Add bottom padding on mobile for nav - smaller on small phones */}
      {isMobile && <div className={`${isSmallPhone ? 'h-12' : 'h-14'} shrink-0`} />}

      {/* Save Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="max-w-[90vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Save Template</DialogTitle>
            <DialogDescription>
              Give your template a name to save it for later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="My Template"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate} className="w-full sm:w-auto">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Presets Dialog */}
      <Dialog open={presetsDialogOpen} onOpenChange={setPresetsDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Template Presets</DialogTitle>
            <DialogDescription>
              Choose a preset to get started quickly.
            </DialogDescription>
          </DialogHeader>
          <TemplatePresets onSelectPreset={handleSelectPreset} />
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <SettingsDialog
        open={settingsDialogOpen}
        onOpenChange={setSettingsDialogOpen}
      />

      {/* Release Notes Dialog */}
      <ReleaseNotesDialog
        open={releaseNotesOpen}
        onOpenChange={setReleaseNotesOpen}
      />
    </div>
    </TooltipProvider>
  );
};

export default Index;
